import * as Api from '@ViewModels';
import { IContactParticipant } from '@ViewModels';
import { action, computed, makeObservable, observable, override } from 'mobx';
import { CustomFormViewModel, FormFieldViewModel } from '../form';
import { AidaNoteViewModel } from '../note';
import { FollowUpOptionIds, IOutcome } from '../phonecall';
import { ILeadFollowUp } from '../queue';
import { IFollowUp, ILeadView, INearbyClients } from './interfaces';

/**
 * Contain and then track information about the lead being displayed to the user. Most updates are applied to the lead
 * immediately, and in part instead of relying on reloading the whole lead. Reloading a lead that was already displayed
 * requires some state rehydration, including but not limited to a phone call that was placed before refreshing.
 *
 * The user still needs to be able to save the outcome of that phone call, and will be reminded to do so each time they
 * visit the lead until the outcome is recoreded.
 */
export class LeadViewModel extends Api.ViewModel {
	public fetchingContacts: boolean;
	public contacts: Api.ContactViewModel[];
	public note: AidaNoteViewModel;
	public settingAttribute = false;
	public decisionMaker: string;
	public clientsNearMe: INearbyClients = null;

	private mLead: ILeadView = null;
	private mCompany: Api.CompanyViewModel = null;
	private mDecisionMakerIds: string[];
	private mAttributesForm: CustomFormViewModel = null;
	private mDealForm: CustomFormViewModel = null;
	private mExecutiveSummary: AidaNoteViewModel;
	private mFollowUpOptions: IFollowUp;
	private mFollowUpSelected: FollowUpOptionIds | ILeadFollowUp = null;

	/** Busy states */
	private mDeletingContact = false;
	private mGettingFollowUpOptions = false;
	private mUpdatingContact = false;
	private mSavingExecutiveSummary = false;
	private mSavingTimezone = false;

	private contactsPageCollectionController: Api.IPageCollectionController<Api.IContact>;

	constructor(userSession: Api.UserSessionContext, lead: ILeadView) {
		super(userSession);

		makeObservable<
			LeadViewModel,
			| 'mLead'
			| 'mCompany'
			| 'mDecisionMakerIds'
			| 'mAttributesForm'
			| 'mDealForm'
			| 'mExecutiveSummary'
			| 'mFollowUpOptions'
			| 'mFollowUpSelected'
			| 'mDeletingContact'
			| 'mGettingFollowUpOptions'
			| 'mUpdatingContact'
			| 'mSavingExecutiveSummary'
			| 'mSavingTimezone'
			| 'mSetLead'
		>(this, {
			fetchingContacts: observable,
			contacts: observable.ref,
			note: observable,
			settingAttribute: observable,
			decisionMaker: observable,
			clientsNearMe: observable,
			mLead: observable,
			mCompany: observable,
			mDecisionMakerIds: observable,
			mAttributesForm: observable,
			mDealForm: observable,
			mExecutiveSummary: observable,
			mFollowUpOptions: observable,
			mFollowUpSelected: observable,
			mDeletingContact: observable,
			mGettingFollowUpOptions: observable,
			mUpdatingContact: observable,
			mSavingExecutiveSummary: observable,
			mSavingTimezone: observable,
			company: computed,
			dealForm: computed,
			executiveSummary: computed,
			pendingOutcome: computed,
			lastDeal: computed,
			nextMeeting: computed,
			nextMeetingFormattedString: computed,
			nextMeetingParticipant: computed,
			previousDeals: computed,
			requiredActivity: computed,
			followUp: computed,
			setFollowUp: action,
			isBusy: override,
			isUpdating: computed,
			attributesForm: computed,
			statusFlags: computed,
			providerUrl: computed,
			providerName: computed,
			relatedLinks: computed,
			abbreviatedTimeZone: computed,
			suggestedTelephonyConfiguration: computed,
			loadDealForm: action,
			createDealForm: action,
			deleteContact: action,
			getContacts: action,
			getFollowUp: action,
			followUpOptions: computed,
			clearFollowUpOptions: action,
			resetContacts: action,
			resetNote: action,
			saveExecutiveSummary: action,
			setAttribute: action,
			setSelectedFollowUp: action,
			selectedFollowUp: computed,
			setTimezone: action,
			toggleDecisionMaker: action,
			update: action,
			mSetLead: action,
		});

		this.mSetLead(lead);
		this.mExecutiveSummary = new AidaNoteViewModel(userSession, lead.executiveSummary);
		if (this.mExecutiveSummary.id) {
			this.mExecutiveSummary.load();
		}

		this.contactsPageCollectionController = new Api.PageCollectionController(
			userSession.webServiceHelper,

			`contact/byCompany/${lead.company.id}`
		);
	}

	get company() {
		return this.mCompany;
	}

	get dealForm() {
		return this.mDealForm;
	}

	get executiveSummary() {
		return this.mExecutiveSummary;
	}

	get pendingOutcome() {
		return this.mLead?.pendingOutcome;
	}

	get leadServedId() {
		return this.mLead?.leadServedId;
	}

	get lastDeal() {
		return this.mLead?.lastDeal;
	}

	get nextMeeting() {
		return this.mLead?.nextMeeting;
	}

	get nextMeetingFormattedString() {
		return this.mLead?.nextMeetingFormattedString;
	}

	get nextMeetingParticipant(): string {
		const person: IContactParticipant = this.mLead?.nextMeeting?.participants?.find(
			x => x._type === 'ContactParticipant'
		);
		if (!person) {
			return 'Unknown';
		}

		return person.firstName;
	}

	get previousDeals() {
		return this.mLead?.previousDeals;
	}

	get requiredActivity() {
		return this.mLead?.requiredActivity;
	}

	get lastDealIsOpen() {
		return this.mLead?.lastDeal && this.mLead.lastDeal.isOpen;
	}

	get followUp() {
		return this.mLead.followUp;
	}

	public setFollowUp(followUp: ILeadFollowUp) {
		this.mLead = {
			...this.mLead,
			followUp,
		};
	}

	public get isBusy() {
		return this.busy || this.loading;
	}

	get isUpdating() {
		return this.company.isBusy;
	}

	get attributesForm() {
		return this.mAttributesForm;
	}

	get statusFlags() {
		return this.mLead.statusFlags;
	}

	get providerUrl() {
		return this.mLead.providerUrl;
	}

	get providerName() {
		return this.mLead.providerName;
	}

	get relatedLinks() {
		return this.mLead.relatedLinks;
	}

	get abbreviatedTimeZone() {
		return this.mLead.abbreviatedTimeZone ?? this.mCompany?.timeZoneAbbr;
	}

	get suggestedTelephonyConfiguration() {
		return this.mLead.suggestedTelephonyConfiguration;
	}

	public isDecisionMaker = (contact: Api.ContactViewModel) => {
		return !!(this.mDecisionMakerIds || []).find(id => id === contact.id);
	};

	public isDecisionMakerById = (id: string) => {
		return !!(this.mDecisionMakerIds || []).find(x => x === id);
	};

	public loadDealForm = (dealId: string) => {
		this.mDealForm = new CustomFormViewModel(
			this.userSession,
			this.mLead,

			`lead/${this.mLead.company.id}/UpdateDeal/${dealId}`
		);
	};

	public createDealForm = () => {
		this.mDealForm = new CustomFormViewModel(this.userSession, this.mLead, `lead/${this.mLead.company.id}/CreateDeal`);
		this.mDealForm.customProperties = { decisionMaker: this.decisionMaker };
	};

	public getClientsNearMe = async (industry?: string) => {
		const result = await this.userSession.webServiceHelper.callWebServiceAsync<INearbyClients>(
			this.composeApiUrl({
				queryParams: industry ? { industry } : undefined,
				urlPath: `lead/${this.company?.id}/nearbyClients`,
			}),
			'GET'
		);
		if (!result.success) {
			throw Api.asApiError(result);
		}

		this.clientsNearMe = result.value;
	};

	public deleteContact = async (contactId: string) => {
		if (!this.mDeletingContact) {
			this.mDeletingContact = true;
			const contact = this.contacts.find(c => c.id === contactId);
			if (contact) {
				const result = await contact.delete();

				if (result.success) {
					this.contacts = this.contacts.filter(c => c.id !== contactId);
					this.mDeletingContact = false;
				} else {
					this.mDeletingContact = false;
					throw Api.asApiError(result);
				}
			}
		}
	};

	public getContacts = (sortDescriptor?: Api.ISortDescriptor, pageSize?: number) => {
		if (this.contactsPageCollectionController.hasAllPages(sortDescriptor, pageSize)) {
			return Api.getEmptyPageControllerResolvedPromise<Api.IContact>();
		}

		this.fetchingContacts = true;
		const promise = this.contactsPageCollectionController.getNext(sortDescriptor, pageSize);
		promise
			.then(result => {
				const fetchResults = result.values.map(x => new Api.ContactViewModel(this.mUserSession, x));
				this.contacts = result.fetchedFirstPage
					? fetchResults
					: fetchResults.length > 0
						? [...this.contacts, ...fetchResults]
						: this.contacts;
				this.fetchingContacts = false;
			})
			.catch(() => {
				this.contacts = [];
				this.fetchingContacts = false;
			});
		return promise;
	};

	public getFollowUp = async (outcome: IOutcome) => {
		if (!this.mGettingFollowUpOptions) {
			this.mGettingFollowUpOptions = true;

			const result = await this.webServiceHelper.callWebServiceAsync<IFollowUp>(
				this.composeApiUrl({ urlPath: `lead/${this.company.id}/PhoneCallOutcome/${outcome.id}/FollowUp` }),
				'GET'
			);

			if (result.success) {
				this.mFollowUpOptions = result.value;
				this.mGettingFollowUpOptions = false;
			} else {
				this.mGettingFollowUpOptions = false;
				throw Api.asApiError(result);
			}
		}
	};

	get followUpOptions() {
		return this.mFollowUpOptions;
	}

	public clearFollowUpOptions = () => {
		this.mFollowUpOptions = null;
	};

	public resetContacts = () => {
		this.fetchingContacts = false;
		this.contacts = [];
		this.contactsPageCollectionController.reset();
	};

	public resetNote = () => {
		this.note = null;
	};

	public saveExecutiveSummary = async (
		note: Api.INote,
		url: string,
		newAttachments?: Api.AttachmentsToBeUploadedViewModel<File>
	) => {
		if (!this.mSavingExecutiveSummary) {
			this.mSavingExecutiveSummary = true;

			// @ts-ignore
			await this.executiveSummary.saveLeadNote(note, url, newAttachments);

			const result = await this.userSession.webServiceHelper.callWebServiceAsync<ILeadView>(
				this.composeApiUrl({ urlPath: `lead/${this.company.id}/ExecutiveSummary` }),
				'PUT',
				{ executiveSummaryNoteId: this.executiveSummary.id }
			);

			if (result.success) {
				this.mSetLead(result.value);
				this.mSavingExecutiveSummary = false;
			} else {
				this.mSavingExecutiveSummary = false;
				throw Api.asApiError(result);
			}
		}
	};

	public setAttribute = async (attribute: FormFieldViewModel<any>, optionId: string) => {
		if (!this.settingAttribute) {
			this.settingAttribute = true;

			const result = await this.userSession.webServiceHelper.callWebServiceAsync<ILeadView>(
				this.composeApiUrl({ urlPath: `lead/${this.company.id}/Lead/Field/${attribute.id}` }),
				'PUT',
				{ value: optionId }
			);

			if (result.success) {
				this.mSetLead(result.value);
				this.settingAttribute = false;
			} else {
				this.settingAttribute = false;
				throw Api.asApiError(result);
			}
		}
	};

	public setSelectedFollowUp = (followUp: FollowUpOptionIds | ILeadFollowUp) => {
		this.mFollowUpSelected = followUp;
	};

	public get selectedFollowUp() {
		return this.mFollowUpSelected;
	}

	public setTimezone = async (timeZone: string) => {
		if (!this.mSavingTimezone) {
			this.mSavingTimezone = true;

			const result = await this.userSession.webServiceHelper.callWebServiceAsync<any>(
				this.composeApiUrl({
					queryParams: { timeZone },
					urlPath: `lead/${this.company.id}/TimeZone`,
				}),
				'PUT'
			);

			if (result.success) {
				this.mCompany.setTimezone(timeZone);
				this.mSavingTimezone = false;
			} else {
				this.mSavingTimezone = false;
				throw Api.asApiError(result);
			}
		}
	};

	public toggleDecisionMaker = async (contact?: Api.ContactViewModel) => {
		if (!this.mUpdatingContact) {
			this.mUpdatingContact = true;
			let contactIds = [...this.mDecisionMakerIds];

			if (this.isDecisionMaker(contact)) {
				contactIds = contactIds.filter(id => id !== contact.id);
			} else {
				contactIds.push(contact.id);
			}

			const result = await this.userSession.webServiceHelper.callWebServiceAsync<ILeadView>(
				this.composeApiUrl({ urlPath: `lead/${this.company.id}/DecisionMakers` }),
				'PUT',
				{ contactIds }
			);

			if (result.success) {
				this.mSetLead(result.value);
				this.mUpdatingContact = false;
			} else {
				this.mUpdatingContact = false;
				throw Api.asApiError(result);
			}
		}
	};

	public update = async (updatedLead: ILeadView) => {
		try {
			await this.company.update(updatedLead.company);
		} catch (err) {
			throw Api.asApiError(err);
		}
	};

	private mSetLead = (lead: ILeadView) => {
		this.mLead = lead;

		this.mCompany = new Api.CompanyViewModel(this.userSession, lead.company);
		this.mDecisionMakerIds = lead.decisionMakersContactIds || [];

		const baseUrl = `lead/${this.mLead.company.id}/Lead`;
		if (!this.mAttributesForm) {
			this.mAttributesForm = new CustomFormViewModel(this.userSession, this.mLead, baseUrl);
		}

		this.mAttributesForm.update(lead.attributesForm, baseUrl);
	};
}
